import React, { useImperativeHandle, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import styled from '@emotion/styled';
import { Text, font } from '../../style-utils/text';
import { palette, spacing, typography, status } from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { FREE_TEXT_MAX } from '../../constants/text-limits';

interface TextEditorProps {
  changeHandler: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      | string,
    fieldName?: string,
    explicitValue?: string
  ) => void;
  defaultValue: string;
  changeHandlerFor: string;
}

// The imperative surface. react-quill-new is UNCONTROLLED here (see the note on
// defaultValue below), so the only way to change what the user is looking at is
// to reach into the editor. Anything that wants to replace the text — today that
// is the Lobbero IQ cleanup hint — goes through this.
export interface TextEditorHandle {
  applyText: (text: string) => void;
}

// Quill's own stylesheet sets `.ql-container { height: 100% }`, and 100% here
// means 100% of THIS element — not "whatever is left under the toolbar". So a
// bare `height: 300px` produced a 42px toolbar stacked on top of a 300px
// container: 342px of content inside a 300px box, overflowing downwards onto
// whatever follows it. On the notes screens that is the Back / Next row.
//
// `margin-bottom: 75px` used to absorb the spill, which is why it only collided
// "sometimes" — the overflow equals the toolbar's height, and the toolbar WRAPS
// to two rows on a narrow viewport, at which point it exceeds 75px and lands on
// the buttons.
//
// Laying the root out as a flex column fixes it at the source: the container
// takes the space the toolbar left rather than a copy of the full height, and
// `.ql-editor` (overflow-y: auto in Quill's CSS) scrolls inside it as intended.
export const StyledQuill = styled(ReactQuill)<{ isSettings?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 300px;
  width: 70%;
  max-width: 600px;
  margin-bottom: ${spacing.md};

  .ql-container {
    /* height:auto + min-height:0 so flex may size it; without min-height a flex
       item refuses to shrink below its content and the overflow returns. */
    flex: 1 1 auto;
    height: auto;
    min-height: 0;
  }

  /* The narrow column is what made the toolbar wrap in the first place. Full
     width on small screens keeps it to one row. */
  ${down('sm')} {
    width: 100%;
  }
`;

// The limit, always on screen — not only once it has been exceeded. Sits right
// under the editor, muted until it matters.
const CounterRow = styled.div<{ over: boolean }>`
  width: 70%;
  max-width: 600px;
  display: flex;
  justify-content: flex-end;
  margin-top: -${spacing.xs};
  margin-bottom: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${({ over }) => (over ? status.alert.fg : palette.slateLight)};

  ${down('sm')} {
    width: 100%;
  }
`;

export const TextEditor = React.forwardRef<TextEditorHandle, TextEditorProps>(
  ({ changeHandler, changeHandlerFor, defaultValue }, ref) => {
    const quillRef = useRef<ReactQuill>(null);

    const [error, setError] = useState<string | null>(null);
    const [length, setLength] = useState<number>(defaultValue?.length ?? 0);

    // The cap is applied to the STORED value (Quill's HTML), because that is
    // exactly what the server caps on the way in. Counting visible characters
    // here instead would show a number that disagrees with the one that can
    // reject the save.
    // eslint-disable-next-line
    const constrictText = (content: string) => {
      setLength(content?.length ?? 0);
      if (content?.length > FREE_TEXT_MAX) {
        setError(`Max characters: ${FREE_TEXT_MAX}`);

        /* TWO DIFFERENT COORDINATE SYSTEMS, ONE OF WHICH IS WRONG HERE.
         *
         * `content` is Quill's HTML, but `deleteText(index, length)` indexes
         * the DOCUMENT — plain characters, no tags. Passing an HTML offset as a
         * document index cut the text at the wrong place: with any formatting
         * at all the markup is longer than the text, so FREE_TEXT_MAX as an
         * HTML offset lands somewhere INSIDE what the writer had already typed
         * and silently deletes the rest of a note that was under the limit.
         *
         * The cap itself stays measured in HTML, because that is what the
         * server caps and what the counter shows. Only the DELETION is done in
         * the editor's own units: cut from wherever the document currently ends
         * back to nothing more than the writer can keep. Quill's length()
         * includes a trailing newline, hence the -1. */
        const quill = quillRef.current?.getEditor();

        if (quill) {
          const docLength = Math.max(quill.getLength() - 1, 0);
          const overBy = content.length - FREE_TEXT_MAX;
          const keep = Math.max(docLength - overBy, 0);

          quill.deleteText(keep, docLength - keep);
        }

        return false;
      } else {
        setError(null); // Clear the error when content is valid

        return true;
      }
    };

    // Write through to the editor AND to the form state, in that order, from one
    // place. Doing only the second is the bug this exists to prevent: the wizard
    // would hold the suggested text while the concierge kept looking at their
    // own — a silent human-out-of-the-loop write into the log.
    useImperativeHandle(ref, () => ({
      applyText: (text: string) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;
        const capped = String(text ?? '').slice(0, FREE_TEXT_MAX);
        quill.setText(capped);
        // Read back what Quill actually holds rather than assuming, so the two
        // sides cannot drift.
        const html = quill.root.innerHTML;
        setLength(html.length);
        setError(html.length > FREE_TEXT_MAX ? `Max characters: ${FREE_TEXT_MAX}` : null);
        changeHandler(html, changeHandlerFor, html);
      },
    }));

    return (
      <>
        <StyledQuill
          ref={quillRef}
          // defaultValue, not value: react-quill-new is deliberately left
          // uncontrolled here (the TRY NOT TO CHANGE note below). That is why
          // applyText above has to reach into the editor — a prop change alone
          // would be ignored.
          defaultValue={defaultValue}
          // the paramater here acts as both the event that is fired and the explicit value that needs to be set
          // this is weird but this is the way react-quill works
          // TRY NOT TO CHANGE
          onChange={e => {
            if (constrictText(e)) {
              changeHandler(e, changeHandlerFor, e);
            }
          }}
        />
        <CounterRow over={length > FREE_TEXT_MAX}>
          {length} / {FREE_TEXT_MAX}
        </CounterRow>
        {error?.length && (
          <div
            style={{
              display: 'flex',
              position: 'relative',
              background: 'lightgray',
            }}
          >
            <Text
              pl={16}
              pr={16}
              width="100%"
              backgroundColour={'lightgray'}
              fontFamily={font.family}
              color={'red'}
              fontSize={font.size.xs}
            >
              {error}
            </Text>
          </div>
        )}
      </>
    );
  }
);

TextEditor.displayName = 'TextEditor';
